import React, { useState, useRef, useEffect } from 'react';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import { Box, Tooltip, ClickAwayListener, FormControlLabel, Checkbox } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { StyledDataGrid } from '../../../../components/DataGrid/DataGridStyles';
import { AutosizeColumnsToggle } from '../../../../components/DataGrid/AutosizeColumnsToggle';
import { SearchBar } from '../../../../components/SearchBar/SearchBar';
import { CustomButton } from '../../../../components/Infra/CustomButton/CustomButton';
import { ViewModeToggle } from '../../../../components/DataFeed/ViewModeToggle/ViewModeToggle';
import { TemplateSelector } from '../../../../components/DataFeed/TemplateSelector/TemplateSelector';
import { useReviewAndUploadStep } from './useReviewAndUploadStep';
import { ReviewAndUploadStepProps } from './types';
import { stepMetadata } from './constants.tsx';
import './ReviewAndUploadStep.scss';

export const ReviewAndUploadStep: React.FC<ReviewAndUploadStepProps> = () => {
  const {
    rows,
    columns,
    isLoading,
    processRowUpdate,
    isUploading,
    selectedRowIds,
    rowSelectionModel,
    handleRowSelectionModelChange,
    handleUploadToIllio,
    viewMode,
    setViewMode,
    handleApplySuccess,
    sessionId,
    templateId,
    setTemplateId,
    isRowDisabled,
    autosizeColumns,
    setAutosizeColumns,
    showDisabledAccounts,
    setShowDisabledAccounts,
    apiRef,
    unidentifiedRowsCount,
    rowsLeftToUpload,
    rowCountsReady,
    getCellClassName,
    hasEditedRows,
    searchTerm,
    handleSearch,
    isPositionsUpload,
    handleColumnWidthChange,
  } = useReviewAndUploadStep();

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const tooltipCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const isRawView = viewMode === 'raw';

  const clearTooltipCloseTimeout = () => {
    if (tooltipCloseTimeoutRef.current) {
      clearTimeout(tooltipCloseTimeoutRef.current);
      tooltipCloseTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearTooltipCloseTimeout();
    };
  }, []);

  const handleTooltipOpen = () => {
    clearTooltipCloseTimeout();
    if (isRawView) {
      setTooltipOpen(true);
    }
  };

  const handleTooltipClose = () => {
    clearTooltipCloseTimeout();
    tooltipCloseTimeoutRef.current = setTimeout(() => {
      setTooltipOpen(false);
    }, 200);
  };

  const handleTooltipClick = () => {
    setTooltipOpen(false);
    setViewMode('processed');
  };

  return (
    <>
    <Box className="reviewAndUploadStep">
      {/* Apply Configuration */}
      {!isPositionsUpload && (
        <TemplateSelector
          onApplySuccess={handleApplySuccess}
          onTemplateApplied={setTemplateId}
          initialTemplateId={templateId}
          sessionId={sessionId}
          hasEditedRows={hasEditedRows}
          statusBadge={
            rowCountsReady ? (
              unidentifiedRowsCount > 0 ? (
                <Tooltip title="Adjust the configuration and apply it to identify remaining instruments">
                  <span className={`badge ${unidentifiedRowsCount > 0 ? 'badgeOrange' : 'badgeGreen'}`}>
                    {`${unidentifiedRowsCount} instruments unidentified`}
                  </span>
                </Tooltip>
              ) : (
                <span className={`badge badgeGreen`}>
                  <CheckCircleIcon sx={{ fontSize: 14, marginRight: 0.5, verticalAlign: 'middle' }} />
                    All instruments identified
                </span>
              )
            ) : null
          }
        />
      )}

      {/* Toolbar */}
      <Box className="toolbar">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ViewModeToggle
            value={viewMode}
            onChange={setViewMode}
            disabled={!isPositionsUpload && !templateId}
          />
          <AutosizeColumnsToggle
            checked={autosizeColumns}
            onChange={setAutosizeColumns}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showDisabledAccounts}
                onChange={(e) => setShowDisabledAccounts(e.target.checked)}
                color="primary"
              />
            }
            label="Show disabled accounts"
            sx={{ ml: '4px' }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {rowCountsReady && (
            <span className={`badge ${rowsLeftToUpload > 0 ? 'badgeOrange' : 'badgeGreen'}`}>
              {rowsLeftToUpload > 0 ? (
                `${rowsLeftToUpload} rows left to upload`
              ) : (
                <>
                  <CheckCircleIcon sx={{ fontSize: 14, marginRight: 0.5, verticalAlign: 'middle' }} />
                  All rows uploaded
                </>
              )}
            </span>
          )}
          <ClickAwayListener onClickAway={handleTooltipClose}>
            <div>
              <Tooltip
                title="Switch to Processed Data view to upload"
                open={tooltipOpen}
                onOpen={handleTooltipOpen}
                onClose={handleTooltipClose}
                disableFocusListener
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -10],
                        },
                      },
                    ],
                  },
                  tooltip: {
                    sx: { fontSize: '0.875rem', cursor: 'pointer' },
                    onClick: handleTooltipClick,
                    onMouseEnter: clearTooltipCloseTimeout,
                    onMouseLeave: handleTooltipClose,
                  },
                }}
              >
                <span
                  style={{ display: 'inline-block' }}
                  onMouseEnter={handleTooltipOpen}
                  onMouseLeave={handleTooltipClose}
                >
                  <CustomButton
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setConfirmOpen(true)}
                    disabled={
                      selectedRowIds.length === 0 ||
                      isUploading ||
                      viewMode !== 'processed'
                    }
                    loading={isUploading}
                  >
                    Upload to Illio
                  </CustomButton>
                </span>
              </Tooltip>
            </div>
          </ClickAwayListener>
        </Box>
      </Box>

      {/* Search */}
      <Box className="searchBarContainer">
        <SearchBar
          onSearch={handleSearch}
          value={searchTerm}
          placeholder="Search rows..."
        />
      </Box>

      {/* Editable Data Grid */}
      <Box className="dataGridContainer">
        <StyledDataGrid
          apiRef={apiRef}
          loading={isLoading}
          rows={rows}
          columns={columns}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={error =>
            console.error('Row update error:', error)
          }
          getRowId={row => row.id}
          checkboxSelection={viewMode === 'processed'}
          disableRowSelectionOnClick
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          isRowSelectable={params => !isRowDisabled(params.row)}
          isCellEditable={params => !isRowDisabled(params.row)}
          getRowClassName={params =>
            isRowDisabled(params.row) ? 'disabledRow' : ''
          }
          getCellClassName={getCellClassName}
          onColumnWidthChange={handleColumnWidthChange}
          hideFooter
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Box>
    </Box>

    <ConfirmationModal
      open={confirmOpen}
      title="Upload to Illio"
      message={`Upload ${selectedRowIds.length} selected row(s) to Illio?`}
      severity="warning"
      confirmText="Upload"
      onConfirm={async () => { await handleUploadToIllio(); setConfirmOpen(false); }}
      onCancel={() => setConfirmOpen(false)}
      submitLoading={isUploading}
    />
    </>
  );
};

export { stepMetadata };
